import { Link } from 'react-router-dom';
import { FaHistory } from 'react-icons/fa';

// Mirrors ActivityLog.js's private toDate/formatWhen so both feeds read identically. Not imported
// from there: those helpers are module-private and ActivityLog.js is owned elsewhere this cycle.
const toDate = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp._seconds) return new Date(timestamp._seconds * 1000);
  if (timestamp.seconds) return new Date(timestamp.seconds * 1000);
  const parsed = new Date(timestamp);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const formatWhen = (timestamp) => {
  const date = toDate(timestamp);
  return date
    ? date.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short', hour12: true })
    : 'Unknown time';
};

// Scheduler jobs run unauthenticated, so they arrive with no email and a uid of 'anonymous'.
const actorOf = (performedBy) => performedBy?.email || (performedBy?.uid && performedBy.uid !== 'anonymous' ? performedBy.uid : 'System');

/**
 * Last 10 real actions across the system. `description` and `repeatCount` are built server-side:
 * read-only requests are already filtered out and repeats of one action already collapsed, so a
 * single page load can no longer fill this feed with identical rows.
 * @param {{entries: Array<{id, description, timestamp, performedBy, repeatCount}>}} props
 */
const RecentActivityFeed = ({ entries = [] }) => {
  const rows = Array.isArray(entries) ? entries : [];

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-800">Recent activity</h2>
      </div>

      {rows.length === 0 ? (
        <div className="text-center text-gray-500 p-6">
          <p>No recent activity.</p>
        </div>
      ) : (
        <div className="px-6 py-2">
          {rows.map((entry, index) => (
            <div key={entry.id || index} className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
              <div className="mt-0.5 shrink-0 rounded-full bg-jims-cyan/10 p-2">
                <FaHistory className="text-jims-cyan-dark text-sm" aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-800 truncate" title={entry.description || ''}>
                  {entry.description || 'Activity recorded'}
                  {entry.repeatCount > 1 && (
                    <span className="ml-2 text-xs font-medium text-gray-500" title={`Repeated ${entry.repeatCount} times`}>
                      &times;{entry.repeatCount}
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-500">
                  {actorOf(entry.performedBy)} &middot; {formatWhen(entry.timestamp)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="px-6 py-3 border-t border-gray-100">
        <Link to="/activity-log" className="text-sm text-jims-cyan-dark hover:text-jims-cyan underline">
          View full activity log
        </Link>
      </div>
    </div>
  );
};

export default RecentActivityFeed;
